<!-- src/routes/about.svelte -->
<script>
  import { onMount } from 'svelte';
  import Button from '../components/common/Button.svelte';
  import logo from '../assets/down-to-earth-logo-arrow-sign.svg';
  import { set_meta } from '../lib/seo.js';
  import { BOOKING_URL } from '../lib/config.js';

  onMount(() => {
    set_meta({
      title: 'About Tom Voltz — The Plain-English AI Translator',
      description: 'Tom Voltz is a real builder who translates AI into plain English and helps small and mid-market teams turn stalled AI into measurable ROI.',
      path: '/about'
    });
  });
</script>

<section class="about_section">
  <div class="content_wrapper">
    <div class="header_section">
      <div class="header_brand">
        <img 
          src={logo}
          alt="Down to Earth Guides Logo" 
          class="header_logo"
        />
        <div class="header_text">
          <h1 class="marker-font">Down to Earth Guides</h1>
          <p class="intro">Plain-English AI for business leaders — from a builder who makes it actually deliver.</p>
        </div>
      </div>
    </div>

    <div class="about_content">
      <div class="about_text">
        <h2>Our Mission</h2>
        <p>Down to Earth Guides breaks down complex technology into clear, practical insights for business leaders — no computer-science degree required. And these days, understanding isn't the hard part. The job is actually getting AI working for you: cut the jargon, find the easy wins, and make a plan you can follow — in plain English.</p>

        <h2>Meet the Team</h2>
        <div class="team_member">
          <h3>Tom Voltz</h3>
          <p class="role">Principal Content Advisor and Presenter · Founder, Usefulbots.com</p>
          <p>Tom is a real builder who translates complex AI into plain English — and then helps you put it to work. He works with owners and execs at small and mid-market companies (20–500 people) who find big consulting intimidating and overpriced, turning stalled AI into measurable wins. He doesn't drop a deck and leave; he makes your people capable.</p>
        </div>

        <h2>Our Approach</h2>
        <p>We believe in:</p>
        <ul>
          <li>Clear, jargon-free explanations</li>
          <li>Real-world examples and applications</li>
          <li>Practical, actionable insights</li>
          <li>Focusing on business impact, not technical details</li>
        </ul>

        <div class="cta_section">
          <h2>Ready to get AI working for you?</h2>
          <p>Book the AI LIFT — a real conversation about your business, the easy wins, and a plan to make it happen. Or start free with the plain-English series.</p>
          <div class="cta_buttons">
            <Button
              href={BOOKING_URL}
              variant="primary"
              size="large"
            >
              Book the AI LIFT
            </Button>
            <Button
              href="/join"
              variant="outline"
              size="large"
            >
              Get the free series
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .about_section {
    padding: 6rem 2rem;
    background: var(--color-background);
  }

  .content_wrapper {
    width: 100%;
    margin: 0 auto;
  }

  .header_section {
    width: 100%;
    margin: 0 auto 3rem;
  }

  .header_brand {
    display: flex;
    align-items: center;
    gap: 2rem;
    justify-content: center;
    text-align: left;
  }

  .header_logo {
    width: 120px;
    height: 120px;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
  }

  .header_text {
    flex: 1;
    max-width: 600px;
  }

  h1 {
    font-size: 3.5rem;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: var(--color-text);
    text-shadow: 2px 2px 0 rgba(0, 0, 0, 0.1);
  }

  .intro {
    font-size: 1.5rem;
    color: var(--color-text-muted);
    margin: 0;
  }

  .about_content {
    max-width: 800px;
    margin: 0 auto;
  }

  .about_text h2 {
    font-size: 2rem;
    color: var(--color-primary);
    margin: 2rem 0 1rem;
  }

  .about_text p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--color-text-muted);
    margin-bottom: 1.5rem;
  }

  .team_member {
    background: var(--color-card-bg);
    padding: 2rem;
    border-radius: 12px;
    margin: 2rem 0;
  }

  .team_member h3 {
    color: var(--color-text);
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .role {
    color: var(--color-primary);
    font-weight: 500;
    margin-bottom: 1rem;
  }

  .about_text ul {
    list-style: none;
    padding: 0;
    margin: 0 0 2rem;
  }

  .about_text ul li {
    font-size: 1.1rem;
    color: var(--color-text-muted);
    margin-bottom: 0.75rem;
    padding-left: 1.5rem;
    position: relative;
  }

  .about_text ul li::before {
    content: "•";
    color: var(--color-primary);
    position: absolute;
    left: 0;
  }

  .cta_section {
    background: var(--color-card-bg);
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    margin-top: 3rem;
  }

  .cta_section h2 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
  }

  .cta_section p {
    margin-bottom: 2rem;
  }

  .cta_buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    .about_section {
      padding: 4rem 1rem;
    }

    .header_brand {
      flex-direction: column;
      text-align: center;
      gap: 1.5rem;
    }

    .header_logo {
      width: 100px;
      height: 100px;
    }

    h1 {
      font-size: 2.5rem;
    }

    .intro {
      font-size: 1.2rem;
    }

    .about_text h2 {
      font-size: 1.5rem;
    }

    .about_text p {
      font-size: 1rem;
    }

    .team_member {
      padding: 1.5rem;
    }

    .team_member h3 {
      font-size: 1.3rem;
    }
  }
</style> 